import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { EVENTS } from "@/lib/wedding-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  name: "",
  email: "",
  attending: true,
  events: [],
  guests_count: 1,
  message: "",
};

const fieldCls =
  "bg-white/70 border-[#D4AF37]/40 focus-visible:ring-[#5D171E] placeholder:text-[#1A1A1A]/35";

export default function Rsvp() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({ attendees: [], party_count: 0, guest_total: 0 });

  const fetchAttendees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rsvp/attendees`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchAttendees();
  }, [fetchAttendees]);

  const toggleEvent = (key) =>
    setForm((f) => ({
      ...f,
      events: f.events.includes(key)
        ? f.events.filter((e) => e !== key)
        : [...f.events, key],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please tell us your name.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp`, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        attending: form.attending,
        events: form.attending ? form.events : [],
        guests_count: form.attending ? Number(form.guests_count) : 1,
        message: form.message.trim() || null,
      });
      toast.success(
        form.attending
          ? "We can't wait to celebrate with you!"
          : "You'll be missed - thank you for letting us know."
      );
      setForm(EMPTY_FORM);
      fetchAttendees();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="rsvp" data-testid="rsvp-section" className="scroll-mt-24 py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-6">
              Répondez S'il Vous Plaît
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1A1A] leading-[1.05]">
              Will you <span className="italic text-[#5D171E]">join us?</span>
            </h2>
            <p className="mt-6 text-base text-[#1A1A1A]/70 max-w-md">
              Kindly respond by early January so we can save you a seat, a plate
              and a spot on the dance floor.
            </p>

            <motion.form
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              onSubmit={submit}
              data-testid="rsvp-form"
              className="gold-frame mt-12 rounded-t-[80px] rounded-b-2xl bg-white/70 border border-[#D4AF37]/40 p-8 md:p-12 space-y-8"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-[0.2em] text-[#5D171E]">
                    Your Name *
                  </label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name"
                    data-testid="rsvp-name-input"
                    className={fieldCls}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-[0.2em] text-[#5D171E]">
                    Email (optional)
                  </label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    data-testid="rsvp-email-input"
                    className={fieldCls}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-xs uppercase tracking-[0.2em] text-[#5D171E]">
                  Attending?
                </label>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, attending: true })}
                    data-testid="rsvp-attending-yes"
                    className={`rounded-full px-6 py-3 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
                      form.attending
                        ? "bg-[#5D171E] text-[#FDFBF7] border-[#5D171E]"
                        : "bg-transparent text-[#5D171E] border-[#D4AF37]/50 hover:border-[#5D171E]"
                    }`}
                  >
                    Joyfully Accepts
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, attending: false })}
                    data-testid="rsvp-attending-no"
                    className={`rounded-full px-6 py-3 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
                      !form.attending
                        ? "bg-[#5D171E] text-[#FDFBF7] border-[#5D171E]"
                        : "bg-transparent text-[#5D171E] border-[#D4AF37]/50 hover:border-[#5D171E]"
                    }`}
                  >
                    Regretfully Declines
                  </button>
                </div>
              </div>

              {form.attending && (
                <>
                  <div className="space-y-3">
                    <label className="text-xs uppercase tracking-[0.2em] text-[#5D171E]">
                      Events You'll Attend
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {EVENTS.map((ev) => (
                        <button
                          key={ev.key}
                          type="button"
                          onClick={() => toggleEvent(ev.key)}
                          data-testid={`rsvp-event-${ev.key}`}
                          className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                            form.events.includes(ev.key)
                              ? "bg-[#D4AF37] text-[#5D171E] border-[#D4AF37]"
                              : "bg-transparent text-[#1A1A1A]/70 border-[#D4AF37]/40 hover:border-[#D4AF37]"
                          }`}
                        >
                          {ev.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-[0.2em] text-[#5D171E]">
                      Guests in Your Party (up to 3 plus-ones)
                    </label>
                    <select
                      value={form.guests_count}
                      onChange={(e) => setForm({ ...form, guests_count: e.target.value })}
                      data-testid="rsvp-guests-select"
                      className="w-full rounded-md border border-[#D4AF37]/40 bg-white/70 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5D171E]"
                    >
                      {[1, 2, 3, 4].map((n) => (
                        <option key={n} value={n}>
                          {n === 1 ? "Just me" : `Me + ${n - 1} ${n - 1 === 1 ? "guest" : "guests"}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#5D171E]">
                  A Note for the Couple (optional)
                </label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Song requests, dietary needs, or just your love..."
                  rows={3}
                  data-testid="rsvp-message-input"
                  className={fieldCls}
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="rsvp-submit-button"
                className="w-full rounded-full bg-[#5D171E] text-[#FDFBF7] text-xs uppercase tracking-[0.25em] py-6 hover:bg-[#471016] transition-colors duration-300"
              >
                {submitting ? "Sending..." : "Send RSVP"}
              </Button>
            </motion.form>
          </div>

          <div id="guests" data-testid="guests-section" className="scroll-mt-24">
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-4 mb-4">
                <Users className="h-5 w-5 text-[#D4AF37]" />
                <h3 className="font-serif text-3xl md:text-4xl tracking-tight text-[#1A1A1A]">
                  Know your <span className="italic text-[#5D171E]">co-guests</span>
                </h3>
              </div>
              <p className="text-sm text-[#1A1A1A]/60 mb-10" data-testid="attendee-count">
                {data.party_count === 0
                  ? "Be the first to RSVP - the guest list awaits."
                  : `${data.party_count} ${data.party_count === 1 ? "party" : "parties"} · ${data.guest_total} guests celebrating so far`}
              </p>

              <div className="grid sm:grid-cols-2 gap-4 max-h-[640px] overflow-y-auto pr-2">
                {data.attendees.map((a, i) => (
                  <motion.div
                    key={`${a.name}-${i}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                    data-testid={`attendee-card-${i}`}
                    className="rounded-2xl border border-[#D4AF37]/30 bg-white/70 p-5 flex items-start gap-4"
                  >
                    <div className="h-11 w-11 shrink-0 rounded-full bg-[#5D171E] text-[#E1C699] font-serif text-xl flex items-center justify-center">
                      {a.name.trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-serif text-lg text-[#1A1A1A] truncate">
                        {a.name}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">
                        Party of {a.guests_count || 1}
                      </p>
                      {a.events?.length > 0 && (
                        <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-[#1A1A1A]/50">
                          {a.events
                            .map((k) => EVENTS.find((e) => e.key === k)?.name || k)
                            .join(" · ")}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
